import { inject, Injectable, signal } from '@angular/core';
import { RegisterUserResponse, SendRegisterUserResponse } from '../interfaces/register-user.response';
import { catchError, map, Observable, throwError } from 'rxjs';
import { StatisticsService } from 'src/app/services/statistics.service';
import { RestService } from 'src/app/services/rest.service';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { registerUserFormAdapter } from '../adapters/register-user-form.adapter';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { environment } from 'src/environments/environment';
import { RegisterUserForm } from '../interfaces';

@Injectable({
  providedIn: 'root'
})
export class RegisterUserService {
  // Injected services
  private _statisticsService = inject(StatisticsService);
  private _restService = inject(RestService);
  private _http = inject(HttpClient);
  private _router = inject(Router);
  private _sessionStorageService = inject(SessionStorageService);
  
  // Signals state
  private _registerUser = signal<RegisterUserResponse | null>(null);
  public registerUser = this._registerUser.asReadonly();
  
  private _registerUserForm = signal<RegisterUserForm | null>(null);
  public registerUserForm = this._registerUserForm.asReadonly();
  
  private _numberOfDaysCustomRoute = signal<number>(0);
  public numberOfDaysCustomRoute = this._numberOfDaysCustomRoute.asReadonly();
  
  private _isLoadingRegisterUserData = signal<boolean>(false);
  public isLoadingRegisterUserData = this._isLoadingRegisterUserData.asReadonly();
  
  private _isLoadingFirstCustomRoute = signal<boolean>(false);
  public isLoadingFirstCustomRoute = this._isLoadingFirstCustomRoute.asReadonly();

  private _cityLocation = signal<string | null>(null);
  public cityLocation = this._cityLocation.asReadonly();
  
  /**
   * Setters
   */
  setRegisterUserData(data: RegisterUserResponse): void {
    this._registerUser.set(data);
    this._sessionStorageService.setItem<RegisterUserResponse>('registerUser', data);
  }
  
  setRegisterUserFormData(data: RegisterUserForm): void {
    this._registerUserForm.set(data);
    this._sessionStorageService.setItem<RegisterUserForm>('registerUserForm', data);
  }
  
  setIsLoadingFirstCustomRoute(isLoading: boolean): void {
    this._isLoadingFirstCustomRoute.set(isLoading);
  }
  
  setNumberOfDaysCustomRoute(numberOfDays: number): void {
    this._numberOfDaysCustomRoute.set(numberOfDays);
  }

  /**
   * Http requests
   */
  getDataRegisterUserServiceSignal() {
    if (this._registerUser()) return;
    
    this._isLoadingRegisterUserData.set(true);
    
    return this.getRegisterUserResponse()
      .pipe(
        catchError((error) => {
          this._isLoadingRegisterUserData.set(false);
          this._router.navigateByUrl("not-found");
          return throwError(() => new Error('HTTP request failed', { cause: error }));
        })
      )
      .subscribe((response: RegisterUserResponse) => {
        this._isLoadingRegisterUserData.set(false);
        this.setRegisterUserData(response);
        
      if (response.location) {
        this._getCityFromCoordinates(response.location.lat, response.location.lng)
          .then(city => {
            this._cityLocation.set(city);
            console.log('=== City set in signal ===', this._cityLocation());
          })
          .catch(error => {
            console.error('Error getting city from coordinates:', error);
            this._cityLocation.set(null);
          })
      } else {
        console.log('=== NO LOCATION DATA ===');
      }
    });
  }
  
  getRegisterUserResponse(): Observable<RegisterUserResponse> {
    // TODO: COMO ES PARA EMT QUE ES APP SÓLO USAMOS EL ENDPOINT PARA APPS
    const url =  this._statisticsService.token 
        ? `${this._restService.apiUrlBase}/api/get-register-app/${this._restService.subDomain}/${this._statisticsService.token}` 
        : `${this._restService.apiUrlBase}/api/get-register-app/${this._restService.subDomain}`
        
    return this._http.get<RegisterUserResponse>(url).pipe(
      map((response: RegisterUserResponse) => {
        if(!environment.production) {
          console.log("getRegisterUserAppResponse:", response);
        }
        return response;
      }),
      catchError((error) => {
        throw new Error(`Error fetching register user data: ${error.message}`);
      })
    )
  }
  
  // Methods
  /**
   * Get city name from latitude and longitude using Google Maps Geocoding API.
   * @param lat Latitude of the location.
   * @param lng Longitude of the location.
   * @returns Promise that resolves to the city name or null if not found.
   */
  private async _getCityFromCoordinates(lat: number, lng: number): Promise<string | null> {
    if(!environment.production) return Promise.resolve(null);
    
    // google api verfication
    if (typeof google === 'undefined' || !google.maps || !google.maps.Geocoder) {
      throw new Error('Google Maps API not available');
    }

    const geocoder = new google.maps.Geocoder();
    const latlng = { lat: Number(lat), lng: Number(lng) };
    
    return new Promise<string | null>((resolve, reject) => {
      geocoder.geocode({ location: latlng }, (results, status) => {
        if(!environment.production) {
          console.log('=== GEOCODING RESPONSE ===');
          console.log('Status:', status);
          console.log('Results:', results);
        }

        if (status === google.maps.GeocoderStatus.OK && results && results.length > 0) {
          const addressComponents = results[0].address_components;
          
          const cityComponent = addressComponents.find(component => 
            component.types.includes('locality') ||
            component.types.includes('administrative_area_level_2') ||
            component.types.includes('administrative_area_level_1')
          );
          
          const cityName = cityComponent?.long_name || null;
          resolve(cityName);
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });
  }
  
  sendRegisterUserData(data: RegisterUserForm): Observable<SendRegisterUserResponse> {
    this._isLoadingFirstCustomRoute.set(true);
    
    const url = `${this._restService.apiUrlBase}/api/get-route-planning`;
    const adaptedData = registerUserFormAdapter(data);
    
    return this._http.post<SendRegisterUserResponse>(url, adaptedData).pipe(
      map((response: SendRegisterUserResponse) => {
        if(!environment.production) {
          console.log("sendRegisterUserData response:", response);
        }
        this._isLoadingFirstCustomRoute.set(true);
        return response;
      }),
      catchError((error) => {
        this._isLoadingFirstCustomRoute.set(false);
        console.error(`Error sending register user data: ${error.message}`);
        return throwError(() => new Error('HTTP request failed', { cause: error }));
      })
    );
  }
}
